// ─────────────────────────────────────────────────────────────────────────────
// BODY SEGMENTATION — per-pixel arm / torso masks for garment occlusion
//
// Approach: landmark-driven polygon masking (no extra model download).
// Uses MediaPipe Pose Z-depth + landmark positions to build arm polygons
// that are then rasterized onto a canvas mask.
//
// Output: ArmMask with left/right arm pixel regions so the renderer can
// composite the garment BEHIND the arms — the #1 realism improvement.
//
// Also loads BodyPix via TF.js CDN on high-end devices for full per-part
// pixel accuracy (hair, head, torso, leftArm, rightArm, leftLeg, rightLeg).
// ─────────────────────────────────────────────────────────────────────────────

import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";

export interface ArmMask {
  data:   Uint8ClampedArray; // 1 = arm pixel, 0 = not arm
  width:  number;
  height: number;
}

export interface BodyPartMask {
  leftArm:   ImageData;
  rightArm:  ImageData;
  torso:     ImageData;
  fullBody:  ImageData;
  width:     number;
  height:    number;
}

// ── Offscreen canvas for mask rendering ──────────────────────────────────────
let _maskCanvas: OffscreenCanvas | HTMLCanvasElement | null = null;
let _maskCtx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null = null;

function getMaskCanvas(w: number, h: number) {
  if (!_maskCanvas || _maskCanvas.width !== w || _maskCanvas.height !== h) {
    if (typeof OffscreenCanvas !== "undefined") {
      _maskCanvas = new OffscreenCanvas(w, h);
      _maskCtx    = _maskCanvas.getContext("2d") as OffscreenCanvasRenderingContext2D;
    } else {
      _maskCanvas = document.createElement("canvas");
      _maskCanvas.width = w;
      _maskCanvas.height = h;
      _maskCtx = _maskCanvas.getContext("2d");
    }
  }
  return { canvas: _maskCanvas, ctx: _maskCtx! };
}

// ── Build arm polygon from pose landmarks ─────────────────────────────────────
// Each arm is a quadrilateral: shoulder → elbow → wrist → (inner edge back up)
function armPolygon(
  shoulder: NormalizedLandmark,
  elbow:    NormalizedLandmark,
  wrist:    NormalizedLandmark,
  w: number,
  h: number,
  isMirrored: boolean,
  thickness = 0.06  // normalized width of arm tube
): [number, number][] {
  const nx = (lm: NormalizedLandmark) =>
    (isMirrored ? 1 - lm.x : lm.x) * w;
  const ny = (lm: NormalizedLandmark) => lm.y * h;

  // Compute perpendicular offset for arm tube width
  const perp = (
    ax: number, ay: number,
    bx: number, by: number,
    t: number
  ): [number, number] => {
    const len = Math.hypot(bx - ax, by - ay) || 1;
    const px  = -(by - ay) / len * t;
    const py  =  (bx - ax) / len * t;
    return [px, py];
  };

  const thickPx = thickness * w;

  const [p0x, p0y] = perp(nx(shoulder), ny(shoulder), nx(elbow), ny(elbow), thickPx);
  const [p1x, p1y] = perp(nx(elbow), ny(elbow), nx(wrist), ny(wrist), thickPx);
  const [p2x, p2y] = perp(nx(elbow), ny(elbow), nx(wrist), ny(wrist), thickPx * 0.8);

  return [
    [nx(shoulder) + p0x,  ny(shoulder) + p0y],
    [nx(elbow)    + p1x,  ny(elbow)    + p1y],
    [nx(wrist)    + p2x,  ny(wrist)    + p2y],
    [nx(wrist)    - p2x,  ny(wrist)    - p2y],
    [nx(elbow)    - p1x,  ny(elbow)    - p1y],
    [nx(shoulder) - p0x,  ny(shoulder) - p0y],
  ];
}

// ── Draw a filled polygon ────────────────────────────────────────────────────
function fillPolygon(
  ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
  points: [number, number][],
  color: string
) {
  if (points.length < 2) return;
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

// ── Main: compute arm mask from pose landmarks ────────────────────────────────
export function computeArmMask(
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
  isMirrored: boolean
): ArmMask {
  const { ctx } = getMaskCanvas(width, height);

  ctx.clearRect(0, 0, width, height);

  const lSh = landmarks[POSE_IDX.LEFT_SHOULDER];
  const lEl = landmarks[POSE_IDX.LEFT_ELBOW];
  const lWr = landmarks[POSE_IDX.LEFT_WRIST];
  const rSh = landmarks[POSE_IDX.RIGHT_SHOULDER];
  const rEl = landmarks[POSE_IDX.RIGHT_ELBOW];
  const rWr = landmarks[POSE_IDX.RIGHT_WRIST];

  // Only draw arm if landmarks are visible and arm is in FRONT of torso
  const torsoZ  = ((lSh?.z ?? 0) + (rSh?.z ?? 0)) / 2;

  if (lSh && lEl && lWr && (lSh.visibility ?? 1) > 0.4) {
    const armZ = ((lEl.z ?? 0) + (lWr.z ?? 0)) / 2;
    if (armZ <= torsoZ + 0.05) { // arm at or in front of torso
      const poly = armPolygon(lSh, lEl, lWr, width, height, isMirrored);
      fillPolygon(ctx, poly, "white");
    }
  }

  if (rSh && rEl && rWr && (rSh.visibility ?? 1) > 0.4) {
    const armZ = ((rEl.z ?? 0) + (rWr.z ?? 0)) / 2;
    if (armZ <= torsoZ + 0.05) {
      const poly = armPolygon(rSh, rEl, rWr, width, height, isMirrored);
      fillPolygon(ctx, poly, "white");
    }
  }

  const imageData = ctx.getImageData(0, 0, width, height);
  // Extract alpha channel as 1-byte mask
  const mask = new Uint8ClampedArray(width * height);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = imageData.data[i * 4 + 3] > 128 ? 1 : 0;
  }

  return { data: mask, width, height };
}

// ── Apply arm mask to an ImageData garment render ────────────────────────────
// Zeroes out alpha where the arm mask is 1 — punches arm-shaped hole in garment
export function applyArmOcclusion(
  garmentImageData: ImageData,
  armMask: ArmMask
): ImageData {
  const out = new ImageData(
    new Uint8ClampedArray(garmentImageData.data),
    garmentImageData.width,
    garmentImageData.height
  );

  const scaleX = garmentImageData.width  / armMask.width;
  const scaleY = garmentImageData.height / armMask.height;

  for (let y = 0; y < garmentImageData.height; y++) {
    for (let x = 0; x < garmentImageData.width; x++) {
      const mx = Math.min(armMask.width  - 1, Math.round(x / scaleX));
      const my = Math.min(armMask.height - 1, Math.round(y / scaleY));
      if (armMask.data[my * armMask.width + mx]) {
        // Arm pixel — remove garment here (arm occludes garment)
        out.data[(y * garmentImageData.width + x) * 4 + 3] = 0;
      }
    }
  }

  return out;
}

// ── BodyPix integration (lazy-loaded on high-end devices) ─────────────────────
// Provides ML-quality per-pixel body part segmentation
// Loaded dynamically to avoid bundle weight

let _bodyPixModel: // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any = null;
let _bodyPixLoading = false;

async function loadTFScript(src: string): Promise<void> {
  if (document.querySelector(`script[src="${src}"]`)) return;
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src; s.async = true;
    s.onload = () => resolve(); s.onerror = reject;
    document.head.appendChild(s);
  });
}

export async function loadBodyPix(): Promise<boolean> {
  if (_bodyPixModel) return true;
  if (_bodyPixLoading) return false;
  _bodyPixLoading = true;

  try {
    // Load TF.js + BodyPix from CDN (lazy — only on high-end)
    await loadTFScript("https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-core@4.20.0/dist/tf-core.min.js");
    await loadTFScript("https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-webgl@4.20.0/dist/tf-backend-webgl.min.js");
    await loadTFScript("https://cdn.jsdelivr.net/npm/@tensorflow-models/body-segmentation@1.0.1/dist/body-segmentation.min.js");

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bodySegmentation = (window as any).bodySegmentation;
    if (!bodySegmentation) throw new Error("bodySegmentation not found on window");

    const model = bodySegmentation.SupportedModels.BodyPix;
    _bodyPixModel = await bodySegmentation.createSegmenter(model, {
      architecture: "MobileNetV1",
      outputStride: 16,
      multiplier: 0.75,
      quantBytes: 2,
    });

    console.log("[BodyPix] ✓ ML per-pixel body segmentation ready");
    return true;
  } catch (err) {
    console.warn("[BodyPix] Failed to load — using landmark polygons fallback", err);
    return false;
  } finally {
    _bodyPixLoading = false;
  }
}

export async function segmentBodyParts(
  video: HTMLVideoElement
): Promise<BodyPartMask | null> {
  if (!_bodyPixModel) return null;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const segmentation = await _bodyPixModel.segmentPeople(video, {
      multiSegmentation: false,
      segmentBodyParts: true,
    });

    if (!segmentation?.length) return null;

    const seg  = segmentation[0];
    const w    = video.videoWidth;
    const h    = video.videoHeight;
    const data = await seg.mask.toImageData();

    // Build per-part ImageData
    // BodyPix part IDs: leftArm=3, rightArm=4, torso=1
    const makePartMask = (partIds: number[]): ImageData => {
      const out = new ImageData(w, h);
      for (let i = 0; i < w * h; i++) {
        const partId = data.data[i]; // grayscale = part ID
        if (partIds.includes(partId)) {
          out.data[i * 4]     = 255;
          out.data[i * 4 + 3] = 255;
        }
      }
      return out;
    };

    const fullBody = new ImageData(w, h);
    for (let i = 0; i < w * h; i++) {
      if (data.data[i] > 0) {
        fullBody.data[i * 4] = 255;
        fullBody.data[i * 4 + 3] = 255;
      }
    }

    return {
      leftArm:  makePartMask([3]),
      rightArm: makePartMask([4]),
      torso:    makePartMask([1, 2]),
      fullBody,
      width:  w,
      height: h,
    };
  } catch {
    return null;
  }
}
